import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Maintenance.css';
import ContentWrapper from './ContentWrapper';
import UniformTable from './UniformTable';
import { toast } from 'react-toastify';

const BASE_URL = process.env.REACT_APP_MAINTAINANCE_API_BASE;

const SERVICE_TYPES = [
  'Oil Change',
  'Tire Rotation',
  'Brake Inspection',
  'Air Filter',
  'Transmission Service',
  'Battery Check',
  'Coolant Flush',
  'Spark Plugs',
  'Fuel Filter',
  'General Inspection',
  'Other',
];


function formatDate(str) {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return str;
  }
}

function formatDateTime(str) {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return str;
  }
}

function StatusBadge({ status }) {
  const s = String(status || 'unknown').toLowerCase();
  const config = {
    overdue: { label: 'Overdue', className: 'mn-badge-new overdue' },
    due_soon: { label: 'Due Soon', className: 'mn-badge-new due-soon' },
    ok: { label: 'Healthy', className: 'mn-badge-new ok' },
    pending: { label: 'Pending', className: 'mn-badge-new pending' },
    unknown: { label: 'No Data', className: 'mn-badge-new unknown' },
  }[s] || { label: 'Unknown', className: 'mn-badge-new unknown' };

  return (
    <span className={config.className}>
      {config.label}
    </span>
  );
}


export default function Maintenance({ devices = [], user }) {

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [query, setQuery] = useState('');


  const [activeTab, setActiveTab] = useState('schedule'); // schedule | history | add | schedule-edit


  const [scheduleData, setScheduleData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingMain, setLoadingMain] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);


  const [histPageKey, setHistPageKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [histLimit] = useState(10);


  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');


  const [form, setForm] = useState({
    serviceType: 'Oil Change',
    odo: '',
    date: new Date().toISOString().slice(0, 10),
    title: '',
    serviceTasks: '',
    invoiceNumber: '',
    serviceCenter: '',
    notes: '',
    intervalKm: '',
    intervalDays: '',
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);


  const [editSchedule, setEditSchedule] = useState({
    serviceType: 'Oil Change',
    intervalKm: '',
    intervalDays: '',
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [deletingSchedule, setDeletingSchedule] = useState(false);

  const userId = user || localStorage.getItem('authUser') || '';


  const loadMain = useCallback(async (device) => {
    if (!device) return;
    setLoadingMain(true);
    setScheduleData(null);
    setHistory([]);
    setHistPageKey(null);
    setHasMore(false);
    try {
      const res = await fetch(
        `${BASE_URL}/maintenance?userId=${encodeURIComponent(userId)}&vehicleId=${encodeURIComponent(device.imei)}&limit=10`
      );
      const json = await res.json();
      let rawSchedule = json.schedule || json.data?.schedule || null;


      if (Array.isArray(rawSchedule)) {
        const normalized = {};
        rawSchedule.forEach(item => {
          if (item.serviceType) normalized[item.serviceType] = item;
        });
        rawSchedule = normalized;
      }

      setScheduleData(rawSchedule);
      const hist = json.history || json.data?.history || [];
      setHistory(hist);
      const nk = json.nextPageKey || json.data?.nextPageKey || null;
      setHistPageKey(nk);
      setHasMore(Boolean(nk));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load maintenance data');
    } finally {
      setLoadingMain(false);
    }
  }, [userId]);


  const loadHistory = useCallback(async (device, fromKey = null, from = '', to = '') => {
    if (!device) return;
    setLoadingHistory(true);
    try {
      let url = `${BASE_URL}/maintenance/history?userId=${encodeURIComponent(userId)}&vehicleId=${encodeURIComponent(device.imei)}&limit=${histLimit}`;
      if (fromKey) url += `&lastKey=${encodeURIComponent(fromKey)}`;
      if (from) url += `&from=${encodeURIComponent(from)}`;
      if (to) url += `&to=${encodeURIComponent(to)}`;
      const res = await fetch(url);
      const json = await res.json();
      const newItems = json.history || json.data?.history || json.items || [];
      if (fromKey) {
        setHistory(prev => [...prev, ...newItems]);
      } else {
        setHistory(newItems);
      }
      const nk = json.nextPageKey || json.data?.nextPageKey || null;
      setHistPageKey(nk);
      setHasMore(Boolean(nk));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load maintenance history');
    } finally {
      setLoadingHistory(false);
    }
  }, [userId, histLimit]);

  useEffect(() => {
    const handlePopState = () => {
      const match = window.location.pathname.match(/^\/maintenance\/(.+)$/);
      if (match) {
        const found = devices.find(d => d.imei === match[1]);
        setSelectedDevice(found || null);
      } else {
        setSelectedDevice(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [devices]);

  useEffect(() => {
    if (selectedDevice) {
      loadMain(selectedDevice);
      setActiveTab('schedule');
    }
  }, [selectedDevice, loadMain]);


  const uploadImage = async (file) => {
    const res = await fetch(`${BASE_URL}/maintenance/upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        vehicleId: selectedDevice.imei,
        filename: file.name,
        contentType: file.type,
      }),
    });
    const json = await res.json();
    const { uploadUrl, key } = json;
    await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
    return key;
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();
    if (!form.serviceType || !form.odo || !form.date) {
      toast.warning('Service type, odometer, and date are required.');
      return;
    }
    setSubmitting(true);
    try {
      let imageKeys = [];
      if (imageFiles.length > 0) {
        toast.info('Uploading images…');
        imageKeys = await Promise.all(imageFiles.map(uploadImage));
      }
      const body = {
        userId,
        vehicleId: selectedDevice.imei,
        serviceType: form.serviceType,
        odo: Number(form.odo),
        date: form.date,
        ...(form.title && { title: form.title }),
        ...(form.serviceTasks && { serviceTasks: form.serviceTasks.split(',').map(s => s.trim()).filter(Boolean) }),
        ...(form.invoiceNumber && { invoiceNumber: form.invoiceNumber }),
        ...(form.serviceCenter && { serviceCenter: form.serviceCenter }),
        ...(form.notes && { notes: form.notes }),
        ...(form.intervalKm && { intervalKm: Number(form.intervalKm) }),
        ...(form.intervalDays && { intervalDays: Number(form.intervalDays) }),
        ...(imageKeys.length > 0 && { images: imageKeys }),
      };
      const res = await fetch(`${BASE_URL}/maintenance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Maintenance record saved!');
      setForm({
        serviceType: 'Oil Change', odo: '', date: new Date().toISOString().slice(0, 10),
        title: '', serviceTasks: '', invoiceNumber: '', serviceCenter: '',
        notes: '', intervalKm: '', intervalDays: '',
      });
      setImageFiles([]);
      await loadMain(selectedDevice);
      setActiveTab('history');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save maintenance record');
    } finally {
      setSubmitting(false);
    }
  };


  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    if (!editSchedule.serviceType) { toast.warning('Select a service type'); return; }
    setSavingSchedule(true);
    try {
      const body = {
        userId,
        vehicleId: selectedDevice.imei,
        serviceType: editSchedule.serviceType,
        ...(editSchedule.intervalKm && { intervalKm: Number(editSchedule.intervalKm) }),
        ...(editSchedule.intervalDays && { intervalDays: Number(editSchedule.intervalDays) }),
      };
      const res = await fetch(`${BASE_URL}/maintenance/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Schedule updated!');
      await loadMain(selectedDevice);

    } catch (err) {
      console.error(err);
      toast.error('Failed to update schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (serviceTypeOverride) => {
    const targetType = serviceTypeOverride || editSchedule.serviceType;
    if (!targetType) {
      toast.warning('Select a service type to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the "${targetType}" maintenance schedule? This action cannot be undone.`)) {
      return;
    }

    setDeletingSchedule(true);
    try {
      const payload = {
        userId: userId,
        vehicleId: selectedDevice.imei,
        serviceType: targetType
      };

      const res = await fetch(`${BASE_URL}/maintenance/schedule`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Schedule deleted!');
      setEditSchedule({
        serviceType: 'Oil Change',
        intervalKm: '',
        intervalDays: '',
      });
      await loadMain(selectedDevice);

    } catch (err) {
      console.error(err);
      toast.error('Failed to delete schedule');
    } finally {
      setDeletingSchedule(false);
    }
  };


  if (!selectedDevice) {
    const filtered = query.trim() === ''
      ? devices
      : devices.filter(d =>
        String(d.name || d.deviceName || '').toLowerCase().includes(query.toLowerCase()) ||
        String(d.imei || '').includes(query)
      );

    const columns = [
      { header: '#', accessor: 'number' },
      { header: 'Name', accessor: 'name' },
      { header: 'IMEI', accessor: 'imei' },
      { header: 'Odometer', accessor: 'odo' },
      { header: 'Action', accessor: 'action', align: 'center' }
    ];

    const rows = filtered.map((d, i) => ({
      number: (
        <span className="badge fleet-table-index">
          {String(i + 1).padStart(2, '0')}
        </span>
      ),
      name: d.name || d.deviceName || `Vehicle ${i + 1}`,
      imei: <span className="mono">{d.imei}</span>,
      odo: typeof d.odometerKm === 'number' ? `${d.odometerKm.toFixed(1)} km` : '—',
      action: (
        <div style={{ textAlign: 'center' }}>
          <button className="soft" onClick={() => {
            setSelectedDevice(d);
            window.history.pushState({}, '', `/maintenance/${d.imei}`);
          }}>
            Select
          </button>
        </div>
      )
    }));

    return (
      <ContentWrapper title="Maintenance">
        <div className="trip-report devices maintenance-devices">
          <div className="devices-desktop">
            <div className="devices-header-row">
              <input
                className="devices-search-input"
                type="text"
                placeholder="Search name, IMEI…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>

            <div className="devices-table-container fleet-table-wrapper">
              {rows.length === 0 ? (
                <div className="align-center fleet-table-empty">No devices found</div>
              ) : (
                <UniformTable columns={columns} data={rows} />
              )}
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="mn-mobile-cards-wrapper">
            {filtered.map((d, i) => (
              <div key={d.imei} className="mn-mobile-device-card-new">
                <div className="mn-mobile-card-header">
                  <span className="mn-index-badge">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="mn-device-name-cell">{d.name || d.deviceName || `Vehicle ${i + 1}`}</h3>
                </div>
                <div className="mn-mobile-card-body">
                  <div className="mn-mobile-info-row">
                    <span className="label">IMEI:</span>
                    <span className="value">{d.imei}</span>
                  </div>
                  <div className="mn-mobile-info-row">
                    <span className="label">Odometer:</span>
                    <span className="value">{typeof d.odometerKm === 'number' ? `${d.odometerKm.toFixed(1)} km` : '—'}</span>
                  </div>
                </div>
                <button
                  className="mn-select-btn-new full-width"
                  onClick={() => {
                    setSelectedDevice(d);
                    window.history.pushState({}, '', `/maintenance/${d.imei}`);
                  }}
                >
                  Select Vehicle
                </button>
              </div>
            ))}
          </div>
        </div>
      </ContentWrapper>
    );
  }


  const deviceName = selectedDevice.name || selectedDevice.deviceName || selectedDevice.imei;

  return (
    <div className="mn-root">
      {/* Header */}
      <div className="mn-header-card">
        <div className="mn-header-left">
          <button className="mn-back-link" onClick={() => window.history.back()}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          </button>

          <div className="mn-device-info-main">
            <div className="mn-device-avatar">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#185050" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 13.1V16c0 .6.4 1 1 1h2" /><circle cx="7" cy="17" r="2" /><path d="M9 17h6" /><circle cx="17" cy="17" r="2" /></svg>
            </div>
            <div className="mn-device-details">
              <div className="mn-device-title-row">
                <h2 className="mn-device-name-new">{deviceName}</h2>
                <span className="mn-status-badge-new">
                  <span className="mn-status-dot" />
                  Active
                </span>
              </div>
              <div className="mn-device-imei-new">IMEI: {selectedDevice.imei}</div>
            </div>
          </div>
        </div>

        <div className="mn-header-actions-new">
          <button
            className="mn-btn-new mn-btn-new--primary"
            onClick={() => setActiveTab('add')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add Record
          </button>
          <button
            className="mn-btn-new mn-btn-new--secondary"
            onClick={() => setActiveTab('schedule-edit')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            Edit Schedule
          </button>
        </div>
      </div>


      {/* Tabs */}
      <div className="mn-tabs-container">
        <div className="mn-tabs-new">
          {[
            { id: 'schedule', label: 'Schedule' },
            { id: 'history', label: 'History' },
            { id: 'add', label: 'Add Record' },
            { id: 'schedule-edit', label: 'Edit Schedule' },
          ].map(tab => (
            <button
              key={tab.id}
              className={`mn-tab-new ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="mn-tab-label">{tab.label}</span>
              {activeTab === tab.id && <div className="mn-tab-underline" />}
            </button>
          ))}
        </div>
      </div>

      {/* ─── SCHEDULE TAB ─── */}
      {activeTab === 'schedule' && (
        <div className="mn-tab-content-new mn-tab-content-schedule">
          {loadingMain ? (
            <div className="mn-loader-wrap"><div className="mn-spinner" /></div>
          ) : !scheduleData || Object.keys(scheduleData).length === 0 ? (
            <div className="mn-empty-state">
              <div className="mn-empty-illustration">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h9" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /><path d="M19 16v6" /><path d="M16 19h6" /></svg>
              </div>
              <h3>No Schedule Found</h3>
              <p>Add a maintenance record or set up a schedule to get started with tracking.</p>
              <button
                className="mn-btn-new mn-btn-new--primary"
                onClick={() => setActiveTab('add')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                Add Record
              </button>
            </div>
          ) : (
            <div className="mn-schedule-layout">
              {/* Timeline / Upcoming section */}
              <div className="mn-timeline-section">
                <h3 className="mn-section-title-new">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                  Upcoming Maintenance
                </h3>
                <div className="mn-timeline-list">
                  {Object.entries(scheduleData)
                    .sort((a, b) => {
                      const dateA = a[1].nextDueDate ? new Date(a[1].nextDueDate) : new Date(8640000000000000);
                      const dateB = b[1].nextDueDate ? new Date(b[1].nextDueDate) : new Date(8640000000000000);
                      return dateA - dateB;
                    })
                    .map(([serviceType, info]) => {
                      const status = info.status || 'unknown';
                      return (
                        <div key={serviceType} className={`mn-timeline-item ${status}`}>
                          <div className="mn-timeline-time">
                            {info.nextDueDate ? formatDate(info.nextDueDate) : 'No Date Set'}
                          </div>
                          <div className="mn-timeline-content">
                            <div className="mn-timeline-header">
                              <span className="mn-timeline-type">{serviceType}</span>
                              <StatusBadge status={status} />
                            </div>
                            <div className="mn-timeline-details">
                              {info.nextDueOdo && <span>Due at: {info.nextDueOdo.toLocaleString()} km</span>}
                              {info.lastServiceDate && <span>Last: {formatDate(info.lastServiceDate)}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Grid View of all services */}
              <div className="mn-services-grid">
                <h3 className="mn-section-title-new">Service Summary</h3>
                <div className="mn-summary-cards-new">
                  {Object.entries(scheduleData).map(([serviceType, info]) => {
                    const status = info.status || 'unknown';
                    return (
                      <div key={serviceType} className={`mn-card-new status-${status}`}>
                        <div className="mn-card-header">
                          <span className="mn-card-type">{serviceType}</span>
                          <StatusBadge status={status} />
                        </div>
                        <div className="mn-card-body">
                          <div className="mn-card-row">
                            <span className="label">Last Service</span>
                            <span className="value">{formatDate(info.lastServiceDate)}</span>
                          </div>
                          <div className="mn-card-row">
                            <span className="label">Current Odo</span>
                            <span className="value">{info.currentOdo != null ? `${info.currentOdo.toLocaleString()} km` : '—'}</span>
                          </div>
                          <div className="mn-card-row">
                            <span className="label">Next Due</span>
                            <span className="value">{formatDate(info.nextDueDate)}</span>
                          </div>
                          {info.nextDueOdo && (
                            <div className="mn-card-row">
                              <span className="label">Next Odo</span>
                              <span className="value">{info.nextDueOdo.toLocaleString()} km</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── HISTORY TAB ─── */}
      {activeTab === 'history' && (
        <div className="mn-tab-content-new">
          <div className="mn-header-with-sub">
            <h3 className="mn-section-title-new">Maintenance History</h3>
          </div>

          {/* Date filter */}
          <div className="mn-filter-bar">
            <div className="mn-filter-group">
              <label className="mn-label">From Date</label>
              <input
                type="date"
                className="mn-input"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
              />
            </div>
            <div className="mn-filter-group">
              <label className="mn-label">To Date</label>
              <input
                type="date"
                className="mn-input"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
              />
            </div>
            <div className="mn-filter-actions-inline">
              <button
                className="mn-btn-new mn-btn-new--primary"
                onClick={() => loadHistory(selectedDevice, null, dateFrom, dateTo)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                Filter
              </button>
              <button
                className="mn-btn-new mn-btn-new--secondary"
                onClick={() => {
                  setDateFrom(''); setDateTo('');
                  loadHistory(selectedDevice, null, '', '');
                }}
              >Clear</button>
            </div>
          </div>

          {loadingHistory && history.length === 0 ? (
            <div className="mn-loader-wrap"><div className="mn-spinner" /></div>
          ) : history.length === 0 ? (
            null
          ) : (
            <>
              <div className="mn-history-list">
                {history.map((rec, i) => (
                  <HistoryRow key={rec.recordId || i} rec={rec} />
                ))}
              </div>
              {loadingHistory && <div className="mn-loader-wrap"><div className="mn-spinner mn-spinner--sm" /></div>}
              {hasMore && !loadingHistory && (
                <div className="mn-pagination">
                  <button
                    className="mn-btn-new mn-btn-new--secondary"
                    onClick={() => loadHistory(selectedDevice, histPageKey, dateFrom, dateTo)}
                  >Load More Records</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── ADD RECORD TAB ─── */}
      {activeTab === 'add' && (
        <div className="mn-tab-content-new">
          <div className="mn-panel-card">
            <div className="mn-panel-card-header">
              <div className="mn-header-with-sub">
                <h3 className="mn-section-title-new">Record Maintenance Service</h3>
              </div>
            </div>
            <div className="mn-panel-card-body">
              <form className="mn-form" onSubmit={handleAddRecord}>
                <div className="mn-form-grid">
                  {/* Required fields */}
                  <div className="mn-form-group">
                    <label className="mn-label">Service Type <span className="mn-required">*</span></label>
                    <select
                      className="mn-input"
                      value={form.serviceType}
                      onChange={e => setForm(f => ({ ...f, serviceType: e.target.value }))}
                      required
                    >
                      {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div className="mn-form-group">
                    <label className="mn-label">Odometer (km) <span className="mn-required">*</span></label>
                    <input
                      className="mn-input"
                      type="number"
                      min="0"
                      placeholder="e.g. 45000"
                      value={form.odo}
                      onChange={e => setForm(f => ({ ...f, odo: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="mn-form-group">
                    <label className="mn-label">Service Date <span className="mn-required">*</span></label>
                    <input
                      className="mn-input"
                      type="date"
                      value={form.date}
                      onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                      required
                    />
                  </div>

                  {/* Optional fields */}
                  <div className="mn-form-group">
                    <label className="mn-label">Title</label>
                    <input
                      className="mn-input"
                      type="text"
                      placeholder="e.g. 45k service"
                      value={form.title}
                      onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    />
                  </div>

                  <div className="mn-form-group">
                    <label className="mn-label">Invoice Number</label>
                    <input
                      className="mn-input"
                      type="text"
                      placeholder="INV-2024-001"
                      value={form.invoiceNumber}
                      onChange={e => setForm(f => ({ ...f, invoiceNumber: e.target.value }))}
                    />
                  </div>

                  <div className="mn-form-group">
                    <label className="mn-label">Service Center</label>
                    <input
                      className="mn-input"
                      type="text"
                      placeholder="Workshop name"
                      value={form.serviceCenter}
                      onChange={e => setForm(f => ({ ...f, serviceCenter: e.target.value }))}
                    />
                  </div>

                  <div className="mn-form-group">
                    <label className="mn-label">Next Interval (km)</label>
                    <input
                      className="mn-input"
                      type="number"
                      min="0"
                      placeholder="e.g. 5000"
                      value={form.intervalKm}
                      onChange={e => setForm(f => ({ ...f, intervalKm: e.target.value }))}
                    />
                  </div>

                  <div className="mn-form-group">
                    <label className="mn-label">Next Interval (days)</label>
                    <input
                      className="mn-input"
                      type="number"
                      min="0"
                      placeholder="e.g. 180"
                      value={form.intervalDays}
                      onChange={e => setForm(f => ({ ...f, intervalDays: e.target.value }))}
                    />
                  </div>

                  <div className="mn-form-group mn-form-group--full">
                    <label className="mn-label">Service Tasks <span className="mn-hint">(comma-separated)</span></label>
                    <input
                      className="mn-input"
                      type="text"
                      placeholder="Oil change, Filter replacement, Tire rotation"
                      value={form.serviceTasks}
                      onChange={e => setForm(f => ({ ...f, serviceTasks: e.target.value }))}
                    />
                  </div>

                  <div className="mn-form-group mn-form-group--full">
                    <label className="mn-label">Notes</label>
                    <textarea
                      className="mn-input mn-textarea"
                      rows={3}
                      placeholder="Additional notes…"
                      value={form.notes}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    />
                  </div>

                  {/* Image upload */}
                  <div className="mn-form-group mn-form-group--full">
                    <label className="mn-label">Attach Images</label>
                    <div
                      className="mn-upload-zone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <span className="mn-upload-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#185050" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                      </span>
                      <span className="mn-upload-text">Click to upload invoice / service images</span>
                      <span className="mn-upload-hint">PNG, JPG, PDF (max 10MB each)</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        style={{ display: 'none' }}
                        onChange={e => setImageFiles(prev => [...prev, ...Array.from(e.target.files || [])])}
                      />
                    </div>
                    {imageFiles.length > 0 && (
                      <div className="mn-file-list">
                        {imageFiles.map((f, i) => (
                          <div key={i} className="mn-file-chip">
                            <span>📄 {f.name}</span>
                            <button
                              type="button"
                              className="mn-file-remove"
                              onClick={() => setImageFiles(prev => prev.filter((_, j) => j !== i))}
                            >×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mn-form-actions">
                  <button
                    type="button"
                    className="mn-btn mn-btn--cancel"
                    onClick={() => setActiveTab('schedule')}
                    disabled={submitting}
                  >Cancel</button>
                  <button
                    type="submit"
                    className="mn-btn mn-btn--save"
                    disabled={submitting}
                  >
                    Save Record
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ─── EDIT SCHEDULE TAB ─── */}
      {activeTab === 'schedule-edit' && (
        <div className="mn-tab-content-new">
          <div className="mn-panel-card">
            <div className="mn-panel-card-header">
              <div className="mn-header-with-sub">
                <h3 className="mn-section-title-new">Update Maintenance Schedule Intervals</h3>
              </div>
            </div>
            <div className="mn-panel-card-body">
              <form className="mn-form" onSubmit={handleUpdateSchedule}>
                <div className="mn-form-grid">
                  <div className="mn-form-group">
                    <label className="mn-label">Service Type <span className="mn-required">*</span></label>
                    <select
                      className="mn-input"
                      value={editSchedule.serviceType}
                      onChange={e => setEditSchedule(s => ({ ...s, serviceType: e.target.value }))}
                      required
                    >
                      {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div className="mn-form-group">
                    <label className="mn-label">Interval (km)</label>
                    <input
                      className="mn-input"
                      type="number"
                      min="0"
                      placeholder="e.g. 5000"
                      value={editSchedule.intervalKm}
                      onChange={e => setEditSchedule(s => ({ ...s, intervalKm: e.target.value }))}
                    />
                  </div>

                  <div className="mn-form-group">
                    <label className="mn-label">Interval (days)</label>
                    <input
                      className="mn-input"
                      type="number"
                      min="0"
                      placeholder="e.g. 180"
                      value={editSchedule.intervalDays}
                      onChange={e => setEditSchedule(s => ({ ...s, intervalDays: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="mn-form-actions">
                  <button
                    type="button"
                    className="mn-btn mn-btn--danger"
                    onClick={handleDeleteSchedule}
                    disabled={savingSchedule || deletingSchedule}
                  >🗑 Delete Schedule</button>
                  <button
                    type="button"
                    className="mn-btn mn-btn--cancel"
                    onClick={() => setActiveTab('schedule')}
                    disabled={savingSchedule || deletingSchedule}
                  >Cancel</button>
                  <button
                    type="submit"
                    className="mn-btn mn-btn--save"
                    disabled={savingSchedule || deletingSchedule}
                  >
                    Update Schedule
                  </button>
                </div>
              </form>
              {/* Existing schedule overview */}
{scheduleData && Object.keys(scheduleData).length > 0 && (
  <div className="mn-section">
    <h3 className="mn-section-title-new">Current Schedule</h3>
    <div className="mn-schedule-summary-list">
      {Object.entries(scheduleData).map(([type, info]) => (
        <div key={type} className="mn-schedule-summary-item">
          <div className="mn-schedule-summary-header">
            <span className="mn-schedule-summary-type">{type}</span>
            <StatusBadge status={info.status || 'unknown'} />
          </div>

          <div className="mn-schedule-summary-body">
            <p className="mn-schedule-summary-line">
              <span className="mn-schedule-summary-label">Current Odo:</span>
              <span className="mn-schedule-summary-value">
                {info.currentOdo != null ? `${info.currentOdo.toLocaleString()} km` : '—'}
              </span>
            </p>
            <p className="mn-schedule-summary-line">
              <span className="mn-schedule-summary-label">Interval (km):</span>
              <span className="mn-schedule-summary-value">
                {info.intervalKm ? `${info.intervalKm.toLocaleString()} km` : '—'}
              </span>
            </p>
            <p className="mn-schedule-summary-line">
              <span className="mn-schedule-summary-label">Interval (days):</span>
              <span className="mn-schedule-summary-value">
                {info.intervalDays ? `${info.intervalDays} days` : '—'}
              </span>
            </p>
          </div>

          <div className="mn-schedule-summary-actions">
            <button
              className="mn-icon-btn mn-icon-btn--edit"
              onClick={() => {
                setEditSchedule({
                  serviceType: type,
                  intervalKm: info.intervalKm || '',
                  intervalDays: info.intervalDays || '',
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              title="Edit this schedule"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </button>
            <button
              className="mn-icon-btn mn-icon-btn--delete"
              onClick={() => handleDeleteSchedule(type)}
              title="Delete this schedule"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
)}
             
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── History Row subcomponent ─────────────────────────────── */
function HistoryRow({ rec }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mn-history-card">
      <div className="mn-history-card__header" onClick={() => setExpanded(e => !e)}>
        <div className="mn-history-card__left">
          <span className="mn-history-card__type">{rec.serviceType || '—'}</span>
          <span className="mn-history-card__date">{formatDate(rec.date || rec.createdAt)}</span>
        </div>
        <div className="mn-history-card__right">
          {rec.odo != null && <span className="mn-odo-badge">{Number(rec.odo).toLocaleString()} km</span>}
          <span className="mn-expand-icon">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>
      {expanded && (
        <div className="mn-history-card__body">
          <div className="mn-history-dashed-box">
            {rec.title && <p className="mn-history-card__title">{rec.title}</p>}

            {rec.serviceCenter && (
              <p className="mn-detail-line">
                <span className="mn-detail-line-label">Service Center:</span>
                <span className="mn-detail-line-value">{rec.serviceCenter}</span>
              </p>
            )}

            {rec.invoiceNumber && (
              <p className="mn-detail-line">
                <span className="mn-detail-line-label">Invoice #:</span>
                <span className="mn-detail-line-value">{rec.invoiceNumber}</span>
              </p>
            )}

            {rec.serviceTasks?.length > 0 && (
              <p className="mn-detail-line mn-detail-line--block">
                <span className="mn-detail-line-label">Tasks Performed:</span>
                <span className="mn-task-chips">
                  {rec.serviceTasks.map((task, i) => (
                    <span key={i} className="mn-task-chip">{task}</span>
                  ))}
                </span>
              </p>
            )}

            {rec.notes && (
              <p className="mn-detail-line">
                <span className="mn-detail-line-label">Notes:</span>
                <span className="mn-detail-line-value">{rec.notes}</span>
              </p>
            )}

            {rec.images?.length > 0 && (
              <p className="mn-detail-line mn-detail-line--block">
                <span className="mn-detail-line-label">Attachments:</span>
                <span className="mn-image-list">
                  {rec.images.map((img, i) => (
                    <a key={i} href={img} target="_blank" rel="noopener noreferrer" className="mn-img-link">
                      📎 View Attachment {i + 1}
                    </a>
                  ))}
                </span>
              </p>
            )}

            {rec.createdAt && (
              <p className="mn-detail-line">
                <span className="mn-detail-line-label">Recorded:</span>
                <span className="mn-detail-line-value">{formatDateTime(rec.createdAt)}</span>
              </p>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
